import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { array, boolean, number, object, ObjectSchema, string } from 'yup'
import { PartiesService } from '../../services/parties-service'
import { ChartBaseFormValues, chartBaseValidationSchema } from './base'

export type CandidateFullName = {
  name: string
  order: number
}

export type GeneralChartRacePartiesFormValues = {
  id?: string
  abbv: string
  name: string
  candidates: number
  candidateNames: CandidateFullName[]
}

export type GeneralChartRaceFormValues = {
  id?: string
  name: string
  type: string
  raceType: string
  writeIns: number
  candidates: number
  candidateNames: CandidateFullName[]
  voteFor: number
  parties: GeneralChartRacePartiesFormValues[]
}

export type GeneralChartPartiesFormValues = {
  id?: number
  abbv: string
  name: string
  selected: boolean
}

export type GeneralChartFormValues = ChartBaseFormValues & {
  parties: GeneralChartPartiesFormValues[]
  races: GeneralChartRaceFormValues[]
  disableStraightParty?: boolean
}

const chartValidationSchema: ObjectSchema<GeneralChartFormValues> = object()
  .shape({
    ...chartBaseValidationSchema,
    disableStraightParty: boolean().required(),
    parties: array()
      .of(
        object({
          id: number(),
          abbv: string().required(),
          name: string().required(),
          selected: boolean().required(),
        })
      )
      .required(),
    races: array()
      .of(
        object().shape(
          {
            id: string(),
            name: string().required(),
            type: string().required(),
            raceType: string().required(),
            writeIns: number()
              .min(0)
              .default(1)
              .required()
              .when(
                ['raceType', 'type', 'candidates'],
                ([raceType, type, candidates], schema) => {
                  return raceType === 'Non-Partisan' &&
                    type === 'Race' &&
                    candidates === 0
                    ? schema.min(
                        1,
                        'Race must have either candidates or write-ins'
                      )
                    : schema
                }
              ),
            voteFor: number()
              .required()
              .when('type', {
                is: 'Race',
                then: (schema) => schema.min(1).required(),
                otherwise: (schema) => schema.optional(),
              }),
            candidates: number()
              .min(0)
              .default(0)
              .required('candidates is required')
              .when(
                ['raceType', 'type', 'writeIns'],
                ([raceType, type, writeIns], schema) => {
                  return raceType === 'Non-Partisan' &&
                    type === 'Race' &&
                    writeIns === 0
                    ? schema.min(
                        1,
                        'Race must have either candidates or write-ins'
                      )
                    : schema
                }
              ),
            parties: array()
              .of(
                object().shape({
                  id: string(),
                  abbv: string().required(),
                  candidates: number().default(0).required(),
                  name: string().required(),
                  candidateNames: array()
                    .of(
                      object({
                        name: string().required(),
                        order: number().required(),
                      })
                    )
                    .default([])
                    .required(),
                })
              )
              .required()
              .default([])
              .test({
                name: 'validate-parties-for-partisan',
                message:
                  'Races must have either write-ins or at least one party with candidates.',
                test: (parties, context) => {
                  const { type, raceType, writeIns } = context.parent

                  // No need to run this test
                  if (type !== 'Race' || raceType !== 'Partisan') {
                    return true
                  }

                  // Check if writeIns is greater than 0
                  if (writeIns > 0) {
                    return true
                  }

                  // Check if at least one party has candidates > 0
                  return parties.some((party) => party.candidates > 0)
                },
              }),
            candidateNames: array()
              .of(
                object({
                  name: string().required(),
                  order: number().required(),
                })
              )
              .default([])
              .required(),
          },
          [
            ['candidates', 'writeIns'],
            ['type', 'candidates'],
            ['writeIns', 'type'],
          ]
        )
      )
      .required()
      .test({
        message: 'Chart must have at least one race or proposal',
        test: (arr) => {
          if (!arr) {
            return false
          }
          return arr!.length > 0
        },
      }),
  })
  .required()

export function useGeneralChartForm() {
  return useForm<GeneralChartFormValues>({
    resolver: yupResolver(chartValidationSchema),
    reValidateMode: 'onChange',
    mode: 'onChange',
    defaultValues: async () => {
      const parties = await PartiesService.getParties()

      return {
        municipality: '',
        county: '',
        precinct: '',
        electionDate: '',
        showCandidateNames: false,
        parties: parties.map((party) => ({
          id: party.id,
          name: party.name,
          abbv: party.abbv,
          selected: false,
        })),
        races: [],
        disableStraightParty: false,
      }
    },
  })
}
